export const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024
export const MAX_IMAGE_SIZE_MB = 5

export const ACCEPTED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const

export const ACCEPTED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'] as const

export type AcceptedImageMimeType = (typeof ACCEPTED_IMAGE_MIME_TYPES)[number]
export type ImageValidationCode = 'invalid-type' | 'too-large'

export interface ImageFileLike {
  name: string
  type: string
  size: number
}

export type ImageValidationResult =
  { valid: true } | { valid: false; code: ImageValidationCode; message: string }

const MIME_TYPE_BY_EXTENSION: Readonly<Record<string, AcceptedImageMimeType>> = {
  '.jpeg': 'image/jpeg',
  '.jpg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
}

function getExtension(filename: string): string {
  const dotIndex = filename.lastIndexOf('.')
  return dotIndex < 0 ? '' : filename.slice(dotIndex).toLowerCase()
}

export function isImageFileLike(value: unknown): value is ImageFileLike {
  if (typeof value !== 'object' || value === null) return false

  const candidate = value as Partial<ImageFileLike>
  return (
    typeof candidate.name === 'string' &&
    typeof candidate.type === 'string' &&
    typeof candidate.size === 'number' &&
    Number.isFinite(candidate.size) &&
    candidate.size >= 0
  )
}

/** Valida simultáneamente extensión, MIME declarado y tamaño. */
export function validateImageFile(file: ImageFileLike): ImageValidationResult {
  const extension = getExtension(file.name)
  const expectedMimeType = MIME_TYPE_BY_EXTENSION[extension]
  const normalizedMimeType = file.type.toLowerCase()

  if (!expectedMimeType || expectedMimeType !== normalizedMimeType) {
    return {
      valid: false,
      code: 'invalid-type',
      message: 'La imagen debe ser JPG, JPEG, PNG o WEBP.',
    }
  }

  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    return {
      valid: false,
      code: 'too-large',
      message: `La imagen no puede superar ${MAX_IMAGE_SIZE_MB} MB.`,
    }
  }

  return { valid: true }
}

export function getImageValidationError(file: ImageFileLike): string | null {
  const result = validateImageFile(file)
  return result.valid ? null : result.message
}

export function isValidImageFile(file: ImageFileLike): boolean {
  return validateImageFile(file).valid
}
